import {
  Box,
  HStack,
  Icon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { FaListUl } from 'react-icons/fa'
import { FiFileMinus } from 'react-icons/fi'
import { LuHistory } from 'react-icons/lu'
import type { TabKey } from '../../../providers/UserPreferencesProvider'
import type {
  MediaCollectionDetailsResponse,
  MediaLibrarySearchCollectionResponse,
} from '../../../interfaces'
import { MLContext, useUserPreferences } from '../../../providers'
import AuditLogs from '../Details/AuditLogs'
import CollectionSummary from '../Details/CollectionSummary'
import CustomMetadataForm from '../Details/CustomMetadataForm'

interface CollectionRightSidebarProps {
  mediaCollection: MediaLibrarySearchCollectionResponse
}

function TabLabel({
  icon,
  children,
  isSelected,
}: {
  icon: React.ComponentType<{ size?: string | number }>
  children: React.ReactNode
  isSelected?: boolean
}): React.ReactElement {
  return (
    <HStack spacing={2} maxW="100%" gap="4px">
      <Icon
        as={icon}
        boxSize={4}
        color={isSelected ? 'appBlue' : 'appBlueyGrey'}
      />
      <Text
        whiteSpace="normal"
        color={isSelected ? 'appBlue' : 'imagekitGray.700'}
      >
        {children}
      </Text>
    </HStack>
  )
}

const CollectionRightSidebar: React.FC<CollectionRightSidebarProps> = ({
  mediaCollection,
}) => {
  const { t } = useTranslation()
  const { prefs, setLastTab } = useUserPreferences()
  const [{ selectedMediaCollections }] = useContext(MLContext)

  const details =
    selectedMediaCollections[0] as unknown as MediaCollectionDetailsResponse

  const tabs: Array<{
    key: TabKey
    label: string
    icon: React.ComponentType<{ size?: string | number }>
  }> = [
    { key: 'summary', label: t('summary', 'Summary'), icon: FaListUl },
    {
      key: 'customMetadata',
      label: t('custom_metadata', 'Custom metadata'),
      icon: FiFileMinus,
    },
    { key: 'history', label: t('history', 'History'), icon: LuHistory },
  ]

  // Find index of lastTab, fallback to summary
  const index = (() => {
    const i = tabs.findIndex(tab => tab.key === prefs.lastTab)
    return i >= 0 ? i : 0
  })()

  return (
    <Tabs
      index={index}
      onChange={i => void setLastTab(tabs[i]?.key ?? 'summary')}
      display="flex"
      flexDirection="column"
      minH="0"
      height="100%"
      size="md"
      isLazy
    >
      <TabList>
        {tabs.map((tab, i) => (
          <Tab
            key={tab.key}
            flexGrow={1}
            borderBottom="2px solid transparent"
            _selected={{
              borderColor: 'appBlue',
              bg: '#f0f5fd',
              fontWeight: 600,
            }}
            _focus={{ outline: 'none' }}
          >
            <TabLabel icon={tab.icon} isSelected={index === i}>
              {tab.label}
            </TabLabel>
          </Tab>
        ))}
      </TabList>

      <TabPanels display="flex" flex="1 1 0" minH={0} overflow="auto">
        {tabs.map(tab => {
          switch (tab.key) {
            case 'summary':
              return (
                <TabPanel key="summary" display="flex" p="0" width="100%">
                  <Box p="16px" w="100%">
                    <CollectionSummary collectionDetails={details} />
                  </Box>
                </TabPanel>
              )
            case 'customMetadata':
              return (
                <TabPanel
                  key="customMetadata"
                  display="flex"
                  flexGrow={1}
                  p="0"
                  minH={0}
                >
                  <Box p="16px" w="100%" minW={0}>
                    <CustomMetadataForm
                      mediaCollection={mediaCollection}
                      mediaCollectionDetails={details}
                    />
                  </Box>
                </TabPanel>
              )
            case 'history':
              return (
                <TabPanel
                  key="history"
                  display="flex"
                  flexGrow={1}
                  p="0"
                  minH={0}
                >
                  <AuditLogs
                    entityId={mediaCollection.mediaCollectionId}
                    entityDetails={details}
                  />
                </TabPanel>
              )
            default:
              return null
          }
        })}
      </TabPanels>
    </Tabs>
  )
}

export default CollectionRightSidebar
